<template>
  <div class="flex gap-4 border-t border-gray-300 py-4 max-md:flex-col md:items-center md:justify-between md:gap-8">
    <div>
      <h3
        :id="`course-title-${course.id}`"
        class="body-md font-semibold"
        v-text="course.title"
      />
      <dl class="body-sm flex flex-wrap items-center gap-x-2">
        <dt
          class="sr-only"
          v-text="`Course Type:`"
        />
        <dd v-text="course.courseDelivery" />

        <template v-if="course.courseDelivery !== 'Online' && (course.startDate || course.endDate)">
          &bull;
          <dt
            class="sr-only"
            v-text="`Date:`"
          />
          <dd v-text="`${course.startDate} - ${course.endDate}`" />
        </template>

        <template v-if="course.courseDelivery === 'Onsite' && course.location">
          &bull;
          <dt
            class="sr-only"
            v-text="`Location:`"
          />
          <dd v-text="`${course.location.city}, ${course.location.state}`" />
        </template>
      </dl>

      <template v-if="status !== 'incomplete' && course.courseDelivery !== 'Onsite' && !course.startDateInFuture">
        <template v-if="completedDateMessage">
          <dl class="body-sm flex flex-wrap items-center gap-x-2">
            <dt class="text-gray-700" v-text="completedDateMessage" />
          </dl>
        </template>

        <template v-if="isExpired">
          <dl class="body-sm flex flex-wrap items-center gap-x-2">
            <dt class="error">Your course access period has expired.</dt>
          </dl>
        </template>
      </template>

      <a
        v-if="status !== 'incomplete' && course.status === 'live'"
        :href="course.url"
        :id="`view-details-link-${course.id}`"
        class="link body-md font-semibold"
        :aria-labelledby="`view-details-link-${course.id} course-title-${course.id}`"
        v-text="`View Details`"
      />
    </div>

    <div class="flex gap-2 md:items-center">
      <template v-if="status === 'incomplete'">
        <button
          type="button"
          :id="`continue-registration-btn-${course.id}`"
          class="btn btn--primary flex-1"
          :disabled="isLoading"
          @click="continueRegistration"
        >
          <span class="relative inline-flex items-center justify-center">
            <BaseLoadingIcon :visible="isLoading" />
            <span
              class="whitespace-nowrap"
              :class="{ 'invisible': isLoading }"
              v-text="'Continue'"
            />
          </span>
        </button>

        <button
          type="button"
          class="btn btn--outline flex-1"
          @click="$emit('confirmDelete', registration.registration_id)"
          v-text="`Delete`"
        />
      </template>
      
      <button
        v-if="status !== 'incomplete'
          && !['Live Online','Onsite'].includes(course.courseDelivery)
          && !course.startDateInFuture"
        type="button"
        :id="`access-course-btn-${course.id}`"
        class="btn btn--primary flex-1"
        :aria-labelledby="`access-course-btn-${course.id} course-title-${course.id}`"
        :disabled="!courseLoaded"
        @click="accessCourse"
      >
        <span class="relative inline-flex items-center justify-center">
          <span class="invisible whitespace-nowrap">Assigning course...</span>
          <span class="absolute inset-0 flex items-center justify-center whitespace-nowrap">
            <template v-if="courseLoaded">
              Access Course
            </template>
            <template v-else>
              Assigning course
              <span class="inline-block font-mono w-[3ch] text-left">{{ dots }}</span>
            </template>
          </span>
        </span>
      </button>

      <button
        v-else-if="status !== 'incomplete'
              && ['Live Online','Onsite'].includes(course.courseDelivery)
              && canAccessByStartWindow"
        type="button"
        :id="`access-course-btn-${course.id}`"
        class="btn btn--primary flex-1"
        :aria-labelledby="`access-course-btn-${course.id} course-title-${course.id}`"
        :disabled="!courseLoaded"
        @click="accessCourse"
      >
        <span class="relative inline-flex items-center justify-center">
          <span class="invisible whitespace-nowrap">Assigning course...</span>
          <span class="absolute inset-0 flex items-center justify-center whitespace-nowrap">
            <template v-if="courseLoaded">
              Access Course
            </template>
            <template v-else>
              Assigning course
              <span class="inline-block font-mono w-[3ch] text-left">{{ dots }}</span>
            </template>
          </span>
        </span>
      </button>

      <!-- TODO: Connect view certificate button -->
      <button
        v-if="status === 'completed'"
        type="button"
        :id="`view-certificate-btn-${course.id}`"
        :aria-labelledby="`view-certificate-btn-${course.id} course-title-${course.id}`"
        class="btn btn--outline whitespace-nowrap flex-1"
        @click="accessCertificate"
        v-text="`View Certificate`"
      />
    </div>
  </div>

  <RefundDisclaimer
    :registration-id="registration.registration_id"
    :show-access-modal="showAccessModal"
    :customer-id="user?.id ?? 0"
    :country-id="registration.personal.country ?? 'US'"
    @close-access-modal="handleCloseAccessModal"
  />
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import { Course } from '@js/api/types/course/Course'
import { triggerRlpCompletedJob } from '@js/api/courses/triggerRlpCompletedJob'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'
import { checkoutLinks } from '@js/utils/links'
import { log } from '@js/utils/logger'
import { env } from '@js/utils/env'

import useAuth from '@js/composables/useAuth'
import useCart from '@js/composables/useCart'
import RefundDisclaimer from '@js/components/account/dashboard/RefundDisclaimer.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'

defineOptions({
  name: 'CourseCard',
})

defineEmits<{
  confirmDelete: [id: number]
  continueRegistration: [id: number]
}>()

const props = defineProps<{
  registration: CustomerRegistration,
  course: Course
  status: 'incomplete' | 'current' | 'completed' | 'enrollment_pending'
}>()

const { setLastRegistration } = useAuth()
const { addItemToCart } = useCart()
const { getAuthUser } = useAuth()

const showAccessModal = ref(false)
const isLoading = ref(false)
const user = computed(() => getAuthUser())
const attestationAccepted = ref(props.registration.attestation || false)

const courseLoaded = computed(() => props.status !== 'enrollment_pending')
const dots = ref('')
const courseStartDateWindow = 30 // days

let dotInterval: number | undefined

const isExpired = computed(() => {
  const exp = props.registration.expiration_date
  return exp ? new Date(exp) < new Date() : false
})

const completedDateMessage = computed(() => {
  const { completed_date } = props.registration
  if (!completed_date) return null

  const date = new Date(completed_date)
  const formatted = date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  return `You completed ${props.course.title} on ${formatted}.`
})

const continueRegistration = async () => {
  isLoading.value = true

  const registration = await addItemToCart(
    props.registration.item.sku,
    props.registration.item.course_option,
    props.registration.craft_entry_id,
    props.registration,
  )

  if (registration) {
    setLastRegistration(props.registration.registration_id)

    window.location.assign(checkoutLinks.CHECKOUT)
  } else {
    log({
      message: 'Course Registration could not be continued',
      file: 'src/wcei/js/components/account/dashboard/CourseCard.vue',
    })
    
    isLoading.value = false
  }
}

/**
 * Handles updating the state of the access modal based on user acceptance of the refund disclaimer.
 */
const handleCloseAccessModal = (accepted: boolean) => {
  if(accepted) {
    attestationAccepted.value = accepted
    accessCourse()
  } else {
    showAccessModal.value = false
  }
}

/**
 * Handles redirecting the user to the course via RLP SSO.
 */
const accessCourse = async () => {
  // If the user has NOT accepted the refund disclaimer, show the modal before proceeding
  if (!attestationAccepted.value) {
    showAccessModal.value = true
  } else {
    showAccessModal.value = false 

    try {
      if (!user.value?.rlpDashboardToken) {
        throw new Error('No RLP token available')
      }

      await triggerRlpCompletedJob({ registrationId: props.registration.registration_id })

      const ssoUrl = `${env.rlp.SSO_URL}${encodeURIComponent(user?.value.rlpDashboardToken)}`

      window.open(ssoUrl, '_blank', 'noopener,noreferrer')
    }
    catch (error) {
      log({
        message: `Error accessing course: ${error}`,
        file: 'src/wcei/js/components/account/dashboard/CourseCard.vue',
      })
    }
  }
}

const accessCertificate = async() => {
  try {
    if (!user.value?.rlpCertificationToken) {
      throw new Error('No RLP token available')
    }

    const ssoUrl = `${env.rlp.SSO_URL}${encodeURIComponent(user?.value.rlpCertificationToken)}`

    window.open(ssoUrl, '_blank', 'noopener,noreferrer')
  }
  catch (error) {
    log({
      message: `Error accessing course certificate: ${error}`,
      file: 'src/wcei/js/components/account/dashboard/CourseCard.vue',
    })
  }
}

/**
 * Determines if the course can be accessed based on the start date.
 */
const canAccessByStartWindow = computed(() => {
  const start = parseCourseDate(props.course?.startDate)
  if (!start) {
    return false; // hide when invalid
  }

  // Today at local midnight
  const todayStart = new Date()
  todayStart.setHours(0, 0, 0, 0)

  // Inclusive end of the window (23:59:59.999 on the Nth day)
  const windowEnd = new Date(todayStart)
  windowEnd.setDate(windowEnd.getDate() + courseStartDateWindow)
  windowEnd.setHours(23, 59, 59, 999)

  return start.getTime() <= windowEnd.getTime()
})

/**
 * Handles parsing various date formats into a Date object
 * @param raw 
 */
function parseCourseDate(raw: unknown): Date | null {
  // Already a Date
  if (raw instanceof Date && !isNaN(+raw)) return new Date(raw)

  // Unix timestamps
  if (typeof raw === 'number') {
    // Heuristic: seconds vs ms
    const ms = raw < 1e12 ? raw * 1000 : raw
    const d = new Date(ms)
    return isNaN(+d) ? null : d
  }

  if (typeof raw === 'string') {
    // Clean up common gotchas
    let s = raw.trim()
    // Replace single space between date & time with 'T' for ISO
    s = s.replace(/^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})$/, '$1T$2')

    // Pure date: make a local-midnight Date to avoid timezone drift
    const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (m) {
      const [, y, mo, d] = m
      return new Date(Number(y), Number(mo) - 1, Number(d), 0, 0, 0, 0)
    }

    // Try as-is
    const d1 = new Date(s);
    if (!isNaN(+d1)) return d1

    // Try assuming UTC if the string looks like date-time without zone
    if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(s)) {
      const d2 = new Date(s + 'Z')
      if (!isNaN(+d2)) return d2
    }
  }

  return null
}


watch(courseLoaded, (loaded) => {
  if (loaded && dotInterval) {
    clearInterval(dotInterval)
    dotInterval = undefined
    dots.value = ''
  }
})

onMounted(() => {
  dotInterval = window.setInterval(() => {
    dots.value = '.'.repeat((dots.value.length % 3) + 1)
  }, 500)
})

onUnmounted(() => {
  if (dotInterval) clearInterval(dotInterval)
})
</script>
